import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import toMoment from '~/lib/toMoment';
import { flashMessage } from '~/app/actions';
import { pagePublicPath } from '~/lib/pagePublicPath';
import { S } from '~/app/ui/tokens';
import Button from '~/app/ui/Button';
import { Page, PageHeader } from '~/app/ui/Page';
import StatusBadge from '~/app/ui/StatusBadge';
import EmptyState from '~/app/ui/EmptyState';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import { RowMenu } from '~/app/ui/Menu';
import { Table, SkeletonRows } from '~/app/ui/Table';
import { pageRequestBlocker } from 'shared/lib/pageRequests';
import { REQUEST_REVIEW } from './PageEditor/composers/useVersionActions';

// Same name as before: other screens refetch 'getPage'.
const PAGE = gql`
  query getPage($id: ID!) {
    page(id: $id) {
      _id
      title
      path
      editorType
      canUserRevert
      canUserRequestApproval
      canUserEdit
      state
      versions {
        _id
        versionId
        updatedAt
        updatedBy
        notes
      }
      commitedVersion
      publishedVersion
    }
  }
`;
const REVERT = gql`
  mutation revertPage($pageId: String!, $version: String) {
    revertPage(pageId: $pageId, version: $version) {
      _id
    }
  }
`;

type Version = { _id: string; versionId: string; updatedAt?: string | null; updatedBy?: string | null; notes?: string | null };
type Pending = { kind: 'restore' | 'review'; version: Version } | null;

const useStyles = createUseStyles({
  version: { fontFamily: S.mono, fontSize: 14, fontWeight: 500 },
  note: { marginTop: 3, fontSize: 13, color: S.muted, maxWidth: 520, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  colWhen: { width: 190, '@media (max-width: 760px)': { display: 'none' } },
  colStatus: { width: 150 },
  colActions: { width: 150, textAlign: 'right', whiteSpace: 'nowrap' },
  muted: { color: S.muted },
  who: { fontSize: 12, color: S.muted, marginTop: 2 },
  actions: { display: 'inline-flex', alignItems: 'center', gap: 4 },
  hidden: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }
});

/**
 * A page's saved versions, newest first: which one is live or in review,
 * previews, and (with the rights) restoring one into the draft or asking for
 * its review. The editor's History panel shows the same, with more.
 */
const VersionsScreen: React.FC = () => {
  const c = useStyles();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch } = useQuery(PAGE, { variables: { id }, fetchPolicy: 'cache-and-network' });
  const [revert] = useMutation(REVERT, { refetchQueries: ['getPage', 'getPages'], awaitRefetchQueries: true });
  const [requestReview] = useMutation(REQUEST_REVIEW, { refetchQueries: ['getPage', 'getPages', 'getPagesPendingApproval', 'getNavData'], awaitRefetchQueries: true });
  const [pending, setPending] = useState<Pending>(null);
  const page = data?.page;
  const versions: Version[] = page ? (page.versions || []).slice().reverse() : [];
  const studio = page?.editorType === 'studio';
  const reviewBlocker = page ? pageRequestBlocker(page, 'review') : null;

  const statusOf = (v: Version) => {
    if (!page) return null;
    if (v.versionId === page.publishedVersion) return <StatusBadge tone="green" label="Live" />;
    if (v.versionId === page.commitedVersion && page.state === 'UNDER_REVIEW') return <StatusBadge tone="amber" label="In review" />;
    if (v.versionId === page.commitedVersion && page.state === 'PENDING_DEPLOY') return <StatusBadge tone="blue" label="Ready to deploy" />;
    if (v.versionId === page.commitedVersion && page.state === 'SCHEDULED') return <StatusBadge tone="blue" label="Scheduled" />;
    return null;
  };

  const run = async () => {
    if (!pending || !page) return;
    const { kind, version } = pending;
    if (kind === 'restore') {
      const restored = await revert({ variables: { pageId: page._id, version: version.versionId } });
      // The server answers null when it couldn't write the page.
      if (!restored.data || !restored.data.revertPage) throw new Error('The restore didn’t go through. Try again.');
      dispatch(flashMessage(`Restored v${version.versionId} into the draft. Nothing changes on the site until it’s published.`));
      return;
    }
    const r = await requestReview({ variables: { pageId: page._id, version: version.versionId } });
    const res = r.data?.requestPublishPage;
    if (!res || res.status !== 200) throw new Error((res && res.message) || 'That didn’t go through. Try again.');
    dispatch(flashMessage(`Asked for review of v${version.versionId}. An approver needs to approve it.`));
  };

  let body: React.ReactNode;
  if (error && !data) body = <tr><td colSpan={4}><EmptyState tone="error" title="Couldn’t load the versions" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState></td></tr>;
  else if (loading && !data) body = <SkeletonRows rows={5} widths={['40%', 110, 70, 90]} />;
  else if (!page) body = <tr><td colSpan={4}><EmptyState icon="search_off" title="Page not found" /></td></tr>;
  else if (versions.length === 0) body = <tr><td colSpan={4}><EmptyState icon="history" title="No saved versions yet">A version is saved when someone publishes or saves one from the editor.</EmptyState></td></tr>;
  else {
    body = versions.map((v) => {
      const when = toMoment(v.updatedAt);
      const preview = page.canUserEdit && !studio ? `/admin/page/${page._id}/preview/${v.versionId}` : `${page.path}?preview=1&v=${encodeURIComponent(v.versionId)}`;
      return (
        <tr key={v._id} data-version={v.versionId}>
          <td>
            <div className={c.version}>v{v.versionId}</div>
            {v.notes && <div className={c.note} title={v.notes}>{v.notes}</div>}
          </td>
          <td className={c.colWhen}>
            {when ? <div title={when.format('MMM D, YYYY h:mm A')}>{when.format('MMM D, YYYY')}</div> : <span className={c.muted}>—</span>}
            {v.updatedBy && <div className={c.who}>by {v.updatedBy}</div>}
          </td>
          <td className={c.colStatus}>{statusOf(v)}</td>
          <td className={c.colActions}>
            <span className={c.actions}>
              <Button size="sm" variant="quiet" icon="visibility" href={preview} target="_blank">Preview</Button>
              <RowMenu
                label={`Actions for v${v.versionId}`}
                testId={`version-menu-${v.versionId}`}
                groups={[
                  page.canUserRequestApproval && v.versionId !== page.publishedVersion
                    ? [{
                      key: 'review',
                      label: 'Ask for review…',
                      icon: 'rate_review',
                      hint: reviewBlocker || 'An approver checks it before it goes live',
                      disabled: !!reviewBlocker,
                      onSelect: () => setPending({ kind: 'review', version: v })
                    }]
                    : [],
                  page.canUserRevert && !studio
                    ? [{ key: 'restore', label: 'Restore into draft…', icon: 'restore', danger: true, onSelect: () => setPending({ kind: 'restore', version: v }) }]
                    : []
                ]}
              />
            </span>
          </td>
        </tr>
      );
    });
  }

  return (
    <Page testId="versions-screen">
      <PageHeader
        back={{ to: '/admin', label: 'Pages' }}
        title={page ? `Versions of “${page.title}”` : 'Versions'}
        description={page ? pagePublicPath(page.path) : undefined}
        actions={page && page.canUserEdit && !studio ? <Button icon="edit" to={`/admin/page/${page._id}`}>Open in editor</Button> : undefined}
      />
      <Table label="Versions" testId="versions-table">
        <thead>
          <tr>
            <th>Version</th>
            <th className={c.colWhen}>Saved</th>
            <th className={c.colStatus}>Status</th>
            <th className={c.colActions}><span className={c.hidden}>Actions</span></th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </Table>
      {pending && pending.kind === 'restore' && (
        <ConfirmDialog
          danger
          title={`Restore v${pending.version.versionId} into the draft?`}
          confirmLabel="Restore into draft"
          onConfirm={run}
          onClose={() => setPending(null)}
          testId="restore-dialog"
        >
          <p>The current draft is replaced by this version. The live page doesn’t change until a version is published.</p>
        </ConfirmDialog>
      )}
      {pending && pending.kind === 'review' && (
        <ConfirmDialog
          title={`Ask for review of v${pending.version.versionId}?`}
          confirmLabel="Ask for review"
          onConfirm={run}
          onClose={() => setPending(null)}
          testId="review-dialog"
        >
          <p>An approver checks it; once approved it goes live with the next deployment.</p>
        </ConfirmDialog>
      )}
    </Page>
  );
};

export default VersionsScreen;
